import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { Menu, X } from 'lucide-react';
import logo from "assets/img/genion_logo.png";

export default function Header() {
  const navItems = [
    { label: "Services", to: "/services" },
    { label: "BBB", to: "/bbb-prediction" },
    // {
    //   label: "Tools",
    //   children: [
    //     { label: "Overview", to: "/platform-overview" },
    //     { label: "Small Molecule Drugs", to: "/small-molecule-drugs" },
    //     { label: "Peptide-Based Drugs", to: "/peptide-based-drugs" },
    //   ]
    // },
    { label: "Cases", to: "/" },
    { label: "Publication", to: "/publication" },
    { label: "News", to: "/news" },
    { label: "Partnership", to: "/partnership" },
    { label: "Membership", to: "/membership" },
    { label: "About", to: "/company-overview" },
    { label: "Contact", to: "/contact" }
  ];

  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <NavbarWrapper>
      <NavbarContainer>
        <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
          <Link to="/">
            <Logo src={logo} alt="Genion Quantum Logo" />
          </Link>

          <NavToggle onClick={() => setMenuOpen(!menuOpen)} aria-label="Toggle navigation">
            {menuOpen ? <X size={28} /> : <Menu size={28} />}
          </NavToggle>
        </div>

        <NavLinks isOpen={menuOpen}>
          {navItems.map(({ label, to, children }) => (
            <NavItem key={label}>
              {to ? (
                <StyledLink to={to} onClick={() => setMenuOpen(false)}>
                  {label}
                </StyledLink>
              ) : (
                <StyledSpan>{label}</StyledSpan>
              )}

              {children && (
                <Dropdown>
                  {children.map(({ label: childLabel, to: childTo }) => (
                    <StyledLink key={childTo} to={childTo} onClick={() => setMenuOpen(false)}>
                      {childLabel}
                    </StyledLink>
                  ))}
                </Dropdown>
              )}
            </NavItem>
          ))}
          {/* Uncomment if you want the Sign In button */}
          {/* <SignInBtn to="http://3.133.131.124:8080/login" target="_blank" onClick={() => setMenuOpen(false)}>
            Sign In
          </SignInBtn> */}
        </NavLinks>
      </NavbarContainer>
    </NavbarWrapper>
  );
}

const NavbarWrapper = styled.nav`
  background: linear-gradient(135deg, rgb(20, 50, 90) 0%, rgb(49, 110, 177) 50%, rgb(78, 158, 163) 100%);
  backdrop-filter: blur(8px);
  padding: 16px 32px;
  position: sticky;
  top: 0;
  z-index: 1000;
  box-shadow: 0 4px 20px rgba(49, 110, 177, 0.15);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const NavbarContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;

  @media (max-width: ${({ theme }) => theme.phoneWidth || '768px'}) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const Logo = styled.img`
  height: 48px;
  object-fit: contain;
`;

const NavToggle = styled.button`
  display: none;
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 8px;
  border-radius: 4px;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }

  @media (max-width: ${({ theme }) => theme.phoneWidth || '768px'}) {
    display: block;
    margin-left: auto;
  }
`;

const NavLinks = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== "isOpen"
})`
  display: flex;
  gap: 24px;
  align-items: center;

  @media (max-width: ${({ theme }) => theme.phoneWidth || '768px'}) {
    flex-direction: column;
    width: 100%;
    margin-top: 12px;
    display: ${({ isOpen }) => (isOpen ? 'flex' : 'none')};
    gap: 16px;
    padding: 16px 0;
    background: linear-gradient(135deg, rgb(44, 99, 160) 0%, rgb(70, 142, 147) 100%);
    border-radius: 8px;
    border: 1px solid rgba(255, 255, 255, 0.1);
  }
`;

const NavItem = styled.div`
  position: relative;

  &:hover > div {
    opacity: 1;
    transform: translateY(0);
    pointer-events: auto;
  }
`;

const StyledLink = styled(Link)`
  text-decoration: none;
  color: white;
  font-weight: 500;
  font-size: ${({ theme }) => theme.fontSizes?.lg || '1rem'};
  position: relative;
  transition: all 0.3s ease;
  padding: 8px 12px;
  border-radius: 4px;

  &:hover {
    color: white;
    background-color: rgba(255, 255, 255, 0.1);
  }

  &::after {
    content: '';
    left: 50%;
    bottom: -2px;
    width: 0;
    height: 2px;
    background-color: white;
    transition: all 0.3s ease;
    transform: translateX(-50%);
  }

  &:hover::after {
    width: 80%;
  }
`;

const StyledSpan = styled.span`
  color: white;
  font-weight: 500;
  font-size: ${({ theme }) => theme.fontSizes?.lg || '1rem'};
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 4px;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

const Dropdown = styled.div`
  opacity: 0;
  transform: translateY(10px);
  pointer-events: none;
  transition: opacity 0.25s ease, transform 0.25s ease;
  position: absolute;
  top: 100%;
  left: 0;
  min-width: 200px;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 8px 25px rgba(49, 110, 177, 0.2);
  background: white;
  z-index: 100;
  border: 1px solid #e2e8f0;

  ${StyledLink} {
    display: block;
    padding: 12px 16px;
    font-size: ${({ theme }) => theme.fontSizes?.md || '0.95rem'};
    color: rgb(49, 110, 177);
    transition: all 0.2s ease;
    border-radius: 0;

    &:hover {
      background: rgba(49, 110, 177, 0.1);
      color: rgb(49, 110, 177);
    }

    &::after {
      display: none;
    }
  }
`;